import React, { useState, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import styles from './FaqItem.module.scss';
import { Heading } from '../index';
import useAuthorMode from '../../lib/helpers/useAuthorMode';

const FaqItem = (props) => {
    const [isOpen, setIsOpen] = useState(false);
    const contentRef = useRef();
    const itemRef = useRef();
    const SELECTORS = {
        CONTAINER: '.fs-faq-container__content',
        ITEM: '.fs-faq-item__btn',
    };

    const currentAuthorMode = useAuthorMode();
    const isEditMode = currentAuthorMode === 'edit';

    const { question, headingLevel, children } = props;

    const toggleAnswer = () => setIsOpen(!isOpen);

    const answerState = isOpen || isEditMode ? 'open' : 'closed';
    const itemName = question && question.trim().split(' ').join('-');
    const triggerId = `trigger-${itemName}`;
    const contentId = `content-${itemName}`;

    const contentElement = contentRef.current;

    useLayoutEffect(() => {
        const faqItemContent = contentRef.current;

        const focusableElements = faqItemContent.querySelectorAll(
            'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [contenteditable], details'
        );

        !isOpen
            ? focusableElements.forEach((element) => element.setAttribute('tabindex', '-1'))
            : focusableElements.forEach((element) => element.removeAttribute('tabindex'));
    }, [isOpen]);

    const contentStyle = {
        height:
            isOpen && contentElement
                ? `${
                      contentElement.clientHeight +
                      (contentElement.offsetTop - contentElement.parentNode.offsetTop)
                  }px`
                : isEditMode
                ? '100%'
                : '0px',
    };

    const getFAQItems = () => {
        const itemElement = itemRef.current;
        const currentFAQContainer = itemElement.closest(SELECTORS.CONTAINER);
        return currentFAQContainer.querySelectorAll(SELECTORS.ITEM);
    };

    const setFocusToFirstFAQ = () => {
        const faqList = getFAQItems();
        const firstItem = faqList && faqList[0];
        firstItem.focus();
    };
    const setFocusToLastFAQ = () => {
        const faqList = getFAQItems();
        const lastItem = faqList && faqList[faqList.length - 1];
        lastItem.focus();
    };

    const keyHandling = (event) => {
        switch (event.key) {
            case 'Home':
                setFocusToFirstFAQ();
                break;
            case 'End':
                setFocusToLastFAQ();
                break;
            default:
                break;
        }
    };

    return (
        <div className={cx(styles.root, 'fs-faq-item__container')}>
            <button
                ref={itemRef}
                id={triggerId}
                aria-controls={contentId}
                className={cx(styles.question, 'fs-faq-item__btn')}
                onClick={toggleAnswer}
                aria-expanded={isOpen}
                onKeyDown={keyHandling}
            >
                <div className={cx(styles['question__content'])}>
                    <Heading text={question} headingLevel={headingLevel} className={styles.title} />
                    <span
                        className={cx(
                            styles['question__icon'],
                            styles[`question__icon--${answerState}`]
                        )}
                        aria-hidden={true}
                    ></span>
                </div>
            </button>
            <div
                id={contentId}
                role="region"
                aria-hidden={!isOpen}
                aria-labelledby={triggerId}
                className={cx(styles.answer, styles[`answer--${answerState}`])}
                style={contentStyle}
            >
                <div ref={contentRef}>{children}</div>
            </div>
        </div>
    );
};

FaqItem.displayName = 'FaqItem';

FaqItem.defaultProps = {
    question: 'question',
};

FaqItem.propTypes = {
    question: PropTypes.string,
};

export default FaqItem;
